import React, { useState } from 'react';
import { Button } from "@/components/ui/button.jsx";
import { Input } from "@/components/ui/input.jsx";
import { Label } from "@/components/ui/label.jsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.jsx";
import { Plus, Trash2, ArrowRight, Cable, Wand2, AlertTriangle, AlertCircle, Info, Pencil } from 'lucide-react';
import { generateOptimalConnections } from '@/utils/connectionOptimizer';
import { analyzeLayout } from '@/utils/layoutAnalyzer';

export default function JumpPlanner({ sections, jumps, onJumpsChange, lightType, voltage }) {
  const [newJump, setNewJump] = useState({
    from_section: '',
    to_section: '',
    length_feet: 5,
    type: 'extension'
  });

  const [editingIndex, setEditingIndex] = useState(null);

  const addJump = () => {
    if (!newJump.from_section || !newJump.to_section) return;
    
    if (editingIndex !== null) {
      // Update existing jump
      const updatedJumps = [...jumps];
      updatedJumps[editingIndex] = { ...newJump, id: jumps[editingIndex].id || `jump-${Date.now()}` };
      onJumpsChange(updatedJumps);
      setEditingIndex(null);
    } else {
      // Add new jump
      onJumpsChange([...jumps, { ...newJump, id: `jump-${Date.now()}` }]);
    }
    
    setNewJump({ from_section: '', to_section: '', length_feet: 5, type: 'extension' });
  };

  const removeJump = (idx) => {
    onJumpsChange(jumps.filter((_, i) => i !== idx));
    if (editingIndex === idx) {
      setEditingIndex(null);
      setNewJump({ from_section: '', to_section: '', length_feet: 5, type: 'extension' });
    }
  };

  const editJump = (idx) => {
    const jump = jumps[idx];
    setNewJump({
      from_section: jump.from_section,
      to_section: jump.to_section,
      length_feet: jump.length_feet,
      type: jump.type
    });
    setEditingIndex(idx);
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setNewJump({ from_section: '', to_section: '', length_feet: 5, type: 'extension' });
  };

  const autoGenerateConnections = () => {
    const optimal = generateOptimalConnections(sections);
    onJumpsChange(optimal);
  };

  const getSectionName = (id) => {
    const section = sections.find(s => s.id === id);
    return section?.name || id;
  };

  // Analyze layout for warnings and recommendations
  const analysis = analyzeLayout(sections, jumps, lightType, voltage);
  const hasIssues = analysis.warnings.length > 0 || analysis.cautions.length > 0;

  if (sections.length < 2) {
    return (
      <div className="flex items-center justify-center h-32 bg-slate-50 rounded-xl border border-dashed border-slate-300">
        <p className="text-sm text-slate-500">Add at least 2 sections to plan jumps</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Caution Section */}
      {hasIssues && (
        <div className="space-y-3">
          {/* High Priority Warnings */}
          {analysis.warnings.map((warning, idx) => (
            <div key={`warn-${idx}`} className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 space-y-1">
                  <p className="font-semibold text-red-900 text-sm">{warning.message}</p>
                  <p className="text-red-700 text-xs">{warning.recommendation}</p>
                </div>
              </div>
            </div>
          ))}

          {/* Medium Priority Cautions */}
          {analysis.cautions.map((caution, idx) => (
            <div key={`caution-${idx}`} className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 space-y-1">
                  <p className="font-semibold text-amber-900 text-sm">{caution.message}</p>
                  <p className="text-amber-700 text-xs">{caution.recommendation}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info Recommendations */}
      {analysis.recommendations.length > 0 && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 space-y-2">
              <p className="font-semibold text-blue-900 text-sm">Installation Tips</p>
              {analysis.recommendations.map((rec, idx) => (
                <div key={`rec-${idx}`} className="text-xs text-blue-700">
                  <span className="font-medium">{rec.message}:</span> {rec.recommendation}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Stats Summary */}
      {analysis.stats.totalPixels > 0 && (
        <div className="p-4 bg-slate-100 rounded-lg">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-xs text-slate-500">Total Pixels</p>
              <p className="text-lg font-bold text-slate-900">{analysis.stats.totalPixels}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Total Length</p>
              <p className="text-lg font-bold text-slate-900">{analysis.stats.totalLength}ft</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Est. Power</p>
              <p className="text-lg font-bold text-slate-900">{analysis.stats.estimatedWatts}W</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Wire Length</p>
              <p className="text-lg font-bold text-slate-900">{analysis.stats.totalWireLength}ft</p>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium text-slate-700">Connections</Label>
        <Button
          variant="outline"
          size="sm"
          onClick={autoGenerateConnections}
          className="text-amber-600 border-amber-300 hover:bg-amber-50"
        >
          <Wand2 className="w-4 h-4 mr-2" />
          Auto-Generate
        </Button>
      </div>
      
      <div className="p-4 bg-slate-50 rounded-xl space-y-4">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium text-slate-700">
            {editingIndex !== null ? 'Edit Connection' : 'Add Connection'}
          </Label>
          {editingIndex !== null && (
            <Button
              variant="ghost"
              size="sm"
              onClick={cancelEdit}
              className="text-slate-600 hover:text-slate-900"
            >
              Cancel
            </Button>
          )}
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          <Select
            value={newJump.from_section}
            onValueChange={(v) => setNewJump({ ...newJump, from_section: v })}
          >
            <SelectTrigger>
              <SelectValue placeholder="From section" />
            </SelectTrigger>
            <SelectContent>
              {sections.map(section => (
                <SelectItem key={section.id} value={section.id}>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: section.color }} />
                    {section.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={newJump.to_section}
            onValueChange={(v) => setNewJump({ ...newJump, to_section: v })}
          >
            <SelectTrigger>
              <SelectValue placeholder="To section" />
            </SelectTrigger>
            <SelectContent>
              {sections.filter(s => s.id !== newJump.from_section).map(section => (
                <SelectItem key={section.id} value={section.id}>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: section.color }} />
                    {section.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-500 mb-1.5 block">Length (ft)</Label>
            <Input
              type="number"
              value={newJump.length_feet}
              onChange={(e) => setNewJump({ ...newJump, length_feet: parseFloat(e.target.value) || 0 })}
              min={1}
            />
          </div>
          
          <div>
            <Label className="text-xs text-slate-500 mb-1.5 block">Type</Label>
            <Select
              value={newJump.type}
              onValueChange={(v) => setNewJump({ ...newJump, type: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="extension">Extension Wire</SelectItem>
                <SelectItem value="jumper">Jumper Cable</SelectItem>
                <SelectItem value="home_run">Home Run</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Button onClick={addJump} className="w-full" disabled={!newJump.from_section || !newJump.to_section}>
          <Plus className="w-4 h-4 mr-2" />
          {editingIndex !== null ? 'Update Connection' : 'Add Connection'}
        </Button>
      </div>

      {jumps.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm font-medium text-slate-700">Current Connections</Label>
          {jumps.map((jump, idx) => (
            <div 
              key={jump.id || idx}
              className={`flex items-center justify-between p-3 rounded-lg border ${
                editingIndex === idx 
                  ? 'bg-amber-50 border-amber-300' 
                  : 'bg-white border-slate-200'
              }`}
            >
              <div className="flex items-center gap-3">
                <Cable className="w-4 h-4 text-slate-400" />
                <span className="font-medium">{getSectionName(jump.from_section)}</span>
                <ArrowRight className="w-4 h-4 text-slate-400" />
                <span className="font-medium">{getSectionName(jump.to_section)}</span>
                <span className="text-sm text-slate-500">
                  {jump.length_feet}ft • {jump.type.replace('_', ' ')}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Button 
                  size="icon" 
                  variant="ghost" 
                  className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                  onClick={() => editJump(idx)}
                  disabled={editingIndex !== null && editingIndex !== idx}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button 
                  size="icon" 
                  variant="ghost" 
                  className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                  onClick={() => removeJump(idx)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
